import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { DialogCustom } from '../../common/dialog-custom';
import { OptionsList } from '../shared/options-list';
import { useEditorKeyboard } from '../shared/use-editor-keyboard';

import type Handsontable from 'handsontable';

import { type SearchOption } from '@/types/search';

/**
 * Component Editor Props interface for Handsontable editor integration
 * Extended to support autocomplete options
 */
interface AutocompleteEditorProps {
  modelValue: { value: unknown };
  onUpdateModelValue: (newValue: string) => void;
  onFinished: () => void;
  /** When set (Handsontable wrapper), Enter saves and moves selection like the default grid editor. */
  onEnterCommit?: (event: KeyboardEvent) => void;
  /** Used with Handsontable wrapper so document-capture Enter does not block opening the editor. */
  handsontable?: Handsontable.Core;
  cellElement?: HTMLElement;
  options?: SearchOption[]; // Autocomplete options
}

/**
 * Autocomplete Editor Component for Handsontable
 * Compatible with ComponentEditorProps interface
 * This component is used with createReactComponentEditorClass
 * Similar to DatepickerEditor, uses DialogCustom for proper positioning
 *
 * Behavior:
 * - User types directly in the cell (TEXTAREA)
 * - This component shows filtered options list based on cell value
 * - Clicking an option sets the value and closes the editor
 */
export const AutocompleteEditor: React.FC<AutocompleteEditorProps> = ({
  modelValue,
  onUpdateModelValue,
  onFinished,
  onEnterCommit,
  handsontable,
  cellElement,
  options = [],
}) => {
  // Get value from modelValue (text in cell)
  const value = typeof modelValue?.value === 'string' ? modelValue.value : '';
  const filterText = value.trim().toLowerCase();

  // Filter options based on cell value
  const filteredOptions = useMemo(() => {
    if (!filterText) {
      // If no filter text, show all options
      return options;
    }
    // Filter options that match the filter text
    return options.filter((opt) => opt.label.toLowerCase().includes(filterText));
  }, [options, filterText]);

  // Find the selected option based on current value
  const selectedOption = useMemo(() => {
    if (!value.trim() || options.length === 0) {
      return null;
    }

    const trimmedValue = value.trim();

    const exactMatch = options.find((opt) => opt.label === trimmedValue);
    if (exactMatch !== undefined) {
      return exactMatch;
    }

    const caseInsensitiveMatch = options.find(
      (opt) => opt.label.trim().toLowerCase() === trimmedValue.toLowerCase()
    );
    return caseInsensitiveMatch ?? null;
  }, [value, options]);

  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectedOptionIndex = useMemo(() => {
    const selectedKey = selectedOption?.key ?? null;
    return filteredOptions.findIndex((opt) => opt.key === selectedKey);
  }, [filteredOptions, selectedOption]);

  /**
   * Handle option click - set value and close editor
   */
  const handleOptionClick = useCallback(
    (option: SearchOption | string) => {
      // Update modelValue through editor callback
      const optionLabel = typeof option === 'string' ? option : option.label;
      onUpdateModelValue(optionLabel);
      // Finish editing - this will trigger Handsontable's afterChange
      onFinished();
    },
    [onUpdateModelValue, onFinished]
  );

  /**
   * Handle Enter key to save current input
   * If value is empty, save empty string (same behavior as clicking outside)
   * If value exists, save current value
   */
  const handleEnterKey = useCallback(
    (event: KeyboardEvent) => {
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        const highlightedOption = filteredOptions.slice(highlightedIndex, highlightedIndex + 1)[0];
        handleOptionClick(highlightedOption as SearchOption | string);
        return;
      }

      // Get current value from modelValue (which reflects TEXTAREA content)
      const currentValue = typeof modelValue?.value === 'string' ? modelValue.value : '';
      const trimmedValue = currentValue.trim();

      // If empty, explicitly set to empty string before finishing
      // This ensures empty value is saved (same as clicking outside)
      if (trimmedValue === '') {
        onUpdateModelValue('');
      }
      if (onEnterCommit) {
        onEnterCommit(event);
      } else {
        onFinished();
      }
    },
    [
      highlightedIndex,
      filteredOptions,
      handleOptionClick,
      modelValue,
      onEnterCommit,
      onUpdateModelValue,
      onFinished,
    ]
  );

  const handleArrowDownKey = useCallback(() => {
    if (filteredOptions.length === 0) return;
    setHighlightedIndex((prev) => {
      if (prev < 0) {
        return selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
      }
      return Math.min(prev + 1, filteredOptions.length - 1);
    });
  }, [filteredOptions.length, selectedOptionIndex]);

  const handleArrowUpKey = useCallback(() => {
    if (filteredOptions.length === 0) return;
    setHighlightedIndex((prev) => {
      if (prev < 0) {
        return selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
      }
      return Math.max(prev - 1, 0);
    });
  }, [filteredOptions.length, selectedOptionIndex]);

  // Handle keyboard events
  useEditorKeyboard({
    onFinished,
    onEnterKey: handleEnterKey,
    onArrowDownKey: handleArrowDownKey,
    onArrowUpKey: handleArrowUpKey,
    handsontable,
  });

  const cellWidth = cellElement?.getBoundingClientRect().width || 300;
  const minWidth = Math.max(cellWidth, 300);

  return (
    <DialogCustom cellElement={cellElement} onFinished={onFinished}>
      <Box
        sx={{
          minWidth: `${minWidth}px`,
          maxWidth: `${minWidth}px`,
        }}
      >
        <OptionsList
          options={filteredOptions}
          selectedValue={selectedOption}
          onOptionClick={handleOptionClick}
          highlightedIndex={highlightedIndex}
          maxHeight='300px'
          fontSize='14px'
          paddingY={1}
          paddingX={2}
          getOptionKey={(option) => {
            const opt = option as SearchOption;
            return typeof opt === 'string' ? opt : String(opt.key);
          }}
          getOptionLabel={(option) => {
            const opt = option as SearchOption;
            return typeof opt === 'string' ? opt : opt.label;
          }}
          isOptionSelected={(option, selected) => {
            const opt = option as SearchOption;
            const sel = selected as SearchOption | null;
            return sel?.key === opt.key;
          }}
        />
      </Box>
    </DialogCustom>
  );
};
